import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { 
  Play, 
  RotateCcw, 
  ChevronLeft, 
  ChevronRight, 
  GitBranch,
  Clock,
  Zap,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  SkipBack,
  ArrowRight
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Simulation Panel - Execution as a First-Class Object
 * 
 * Four persistent regions:
 * A. Execution Timeline (Primary) - Event-centric, immutable entries
 * B. Current Execution Snapshot - The 'now', derived from timeline head
 * C. Event Injection Console - Structured, only valid events
 * D. Branching & Time Control - Step, replay, fork
 */
const SimulationPanel = ({ machineId, machine, onTraceUpdate }) => {
  const [session, setSession] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [branches, setBranches] = useState([]);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [eventPayload, setEventPayload] = useState('{}');
  const [runtimeProfile, setRuntimeProfile] = useState('simulation');
  const [loading, setLoading] = useState(false);
  const [selectedStep, setSelectedStep] = useState(null);
  const [availableCaps, setAvailableCaps] = useState([]);

  const apiUrl = process.env.REACT_APP_BACKEND_URL;
  
  // Notify parent of trace updates
  const updateTrace = useCallback((newTimeline) => {
    setTimeline(newTimeline);
    if (onTraceUpdate) {
      onTraceUpdate(newTimeline);
    }
  }, [onTraceUpdate]);

  // Start simulation session
  const startSimulation = useCallback(async () => {
    if (!machineId) {
      toast.error('No machine selected');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(
        `${apiUrl}/api/machines/${machineId}/simulation/start`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ runtime_profile: runtimeProfile })
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to start simulation');
      }
      
      const data = await response.json();
      
      setSession({ id: data.session_id, profile: data.runtime_profile });
      setSnapshot(data.snapshot);
      setAvailableEvents(data.available_events || []);
      setAvailableCaps(data.available_capabilities || []);
      updateTrace([]);
      setBranches([{ name: 'main', length: 0 }]);
      setSelectedStep(null);
      setSelectedEvent('');
      
      toast.success(`Simulation started (${data.runtime_profile})`);
    } catch (error) {
      console.error('Start simulation error:', error);
      toast.error('Failed to start simulation');
    } finally {
      setLoading(false);
    }
  }, [machineId, runtimeProfile, apiUrl]);

  // Dispatch event
  const dispatchEvent = useCallback(async () => {
    if (!session?.id || !selectedEvent) {
      toast.error('Select an event first');
      return;
    }
    
    let payload = {};
    try {
      if (eventPayload && eventPayload !== '{}') {
        payload = JSON.parse(eventPayload);
      }
    } catch (e) {
      toast.error('Invalid JSON payload');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(
        `${apiUrl}/api/simulation/${session.id}/dispatch`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ event_name: selectedEvent, payload })
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to dispatch event');
      }
      
      const data = await response.json();
      
      setSnapshot(data.snapshot);
      updateTrace([...timeline, data.entry]);
      setAvailableEvents(data.available_events || []);
      setSelectedEvent('');
      setSelectedStep(null);
      
      if (data.entry?.transition_taken) {
        toast.success(`→ ${data.entry.to_state}`);
      } else {
        toast.warning('Event not handled in current state');
      }
    } catch (error) {
      console.error('Dispatch error:', error);
      toast.error('Failed to dispatch event');
    } finally {
      setLoading(false);
    }
  }, [session, selectedEvent, eventPayload, apiUrl]);

  // Step backward
  const stepBackward = useCallback(async () => {
    if (!session?.id) return;
    
    try {
      const response = await fetch(
        `${apiUrl}/api/simulation/${session.id}/step-back`,
        { method: 'POST' }
      );
      
      if (!response.ok) {
        toast.info('Already at initial state');
        return;
      }
      
      const data = await response.json();
      setSnapshot(data.snapshot);
      setAvailableEvents(data.available_events || []);
      toast.info('Stepped backward');
    } catch (error) {
      toast.error('Cannot step back further');
    }
  }, [session, apiUrl]);

  // Replay to step
  const replayToStep = useCallback(async (stepIndex) => {
    if (!session?.id) return;
    
    try {
      const response = await fetch(
        `${apiUrl}/api/simulation/${session.id}/replay`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ step_index: stepIndex })
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to replay');
      }
      
      const data = await response.json();
      setSnapshot(data.snapshot);
      setAvailableEvents(data.available_events || []);
      setSelectedStep(stepIndex);
      toast.info(`Replayed to step ${stepIndex}`);
    } catch (error) {
      toast.error('Failed to replay');
    }
  }, [session, apiUrl]);

  // Fork timeline
  const forkTimeline = useCallback(async () => {
    if (!session?.id || selectedStep === null) {
      toast.error('Select a step to fork from');
      return;
    }
    
    const branchName = `fork_${Date.now().toString(36)}`;
    try {
      const response = await fetch(
        `${apiUrl}/api/simulation/${session.id}/fork`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            at_step: selectedStep, 
            branch_name: branchName,
            description: `Fork at step ${selectedStep}`
          })
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fork');
      }
      
      const data = await response.json();
      setSnapshot(data.snapshot);
      setBranches(data.branches || []);
      toast.success(`Forked to ${branchName}`);
    } catch (error) {
      toast.error('Failed to fork timeline');
    }
  }, [session, selectedStep, apiUrl]);

  // Reset simulation
  const resetSimulation = useCallback(async () => {
    if (!session?.id) return;
    
    try {
      const response = await fetch(
        `${apiUrl}/api/simulation/${session.id}/reset`,
        { method: 'POST' }
      );
      
      if (!response.ok) {
        throw new Error('Failed to reset');
      }
      
      const data = await response.json();
      setSnapshot(data.snapshot);
      setTimeline([]);
      setAvailableEvents(data.available_events || []);
      setSelectedStep(null);
      setSelectedEvent('');
      toast.info('Simulation reset');
    } catch (error) {
      toast.error('Failed to reset');
    }
  }, [session, apiUrl]);

  // Export trace
  const exportTrace = useCallback(async () => {
    if (!session?.id) return;
    
    try {
      const response = await fetch(
        `${apiUrl}/api/simulation/${session.id}/export-trace`
      );
      
      if (!response.ok) {
        throw new Error('Failed to export');
      }
      
      const data = await response.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${machine?.name || 'machine'}_trace_${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Trace exported');
    } catch (error) {
      toast.error('Failed to export trace');
    }
  }, [session, machine, apiUrl]);

  // End session
  const endSession = useCallback(() => {
    setSession(null);
    setSnapshot(null);
    setTimeline([]);
    setBranches([]);
    setAvailableEvents([]);
    setSelectedStep(null);
  }, []);

  // Not started state
  if (!session) {
    return (
      <div className="h-full flex flex-col p-4" data-testid="simulation-panel">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4 max-w-md">
            <div className="w-16 h-16 mx-auto rounded-full bg-blue-500/20 flex items-center justify-center">
              <Play className="w-8 h-8 text-blue-400" />
            </div>
            <h3 className="text-lg font-bold">Controlled Execution Environment</h3>
            <p className="text-sm text-gray-400">
              Start a simulation session to execute the machine step-by-step with full visibility into transitions, guards, effects, and capability checks.
            </p>
            
            <div className="space-y-3 pt-4">
              <div>
                <Label className="text-xs text-gray-500">Runtime Profile</Label>
                <Select value={runtimeProfile} onValueChange={setRuntimeProfile}>
                  <SelectTrigger className="w-full mt-1" data-testid="profile-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="simulation">Simulation (Log.Trace only)</SelectItem>
                    <SelectItem value="offline_browser">Offline Browser (Local storage)</SelectItem>
                    <SelectItem value="online_browser">Online Browser (HTTP + Local)</SelectItem>
                    <SelectItem value="worker">Worker (Full capabilities)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                onClick={startSimulation} 
                disabled={loading || !machineId} 
                className="w-full"
                data-testid="start-simulation-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                {loading ? 'Starting...' : 'Start Simulation'}
              </Button>
              
              {!machineId && (
                <p className="text-xs text-yellow-400">Select a machine first</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="simulation-panel-active">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 bg-black/30 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <Badge variant="outline" className="text-green-400 border-green-500/30">
            <Clock className="w-3 h-3 mr-1" />
            {session.profile}
          </Badge>
          <span className="text-xs text-gray-500">
            {timeline.length} steps
          </span>
        </div>
        <div className="flex items-center space-x-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" onClick={exportTrace} className="h-7">
                  <Download className="w-3.5 h-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Export Trace</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" onClick={resetSimulation} className="h-7">
                  <RotateCcw className="w-3.5 h-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Reset</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button variant="ghost" size="sm" onClick={endSession} className="h-7 text-xs">
            End
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden grid grid-cols-2 gap-0">
        {/* LEFT: Timeline + Controls */}
        <div className="border-r border-white/10 flex flex-col overflow-hidden">
          {/* Current State */}
          <div className="p-3 border-b border-white/10 bg-gradient-to-r from-blue-500/10 to-purple-500/10 flex-shrink-0">
            <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Current State</div>
            <div className="text-2xl font-bold text-blue-400">{snapshot?.active_state || 'Unknown'}</div>
            {snapshot?.deferred_effects?.length > 0 && (
              <div className="mt-2 flex items-center text-xs text-yellow-400">
                <AlertTriangle className="w-3 h-3 mr-1" />
                {snapshot.deferred_effects.length} deferred effect(s)
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="px-3 py-2 text-[10px] text-gray-500 uppercase tracking-wider border-b border-white/10 flex-shrink-0">
              Execution Timeline
            </div>
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-1">
                {timeline.length === 0 ? (
                  <div className="text-center py-8 text-xs text-gray-500">
                    No events dispatched yet
                  </div>
                ) : (
                  timeline.map((entry, idx) => (
                    <TimelineEntry 
                      key={idx} 
                      entry={entry} 
                      index={idx}
                      selected={selectedStep === idx}
                      onSelect={() => {
                        setSelectedStep(idx);
                        replayToStep(idx);
                      }}
                    />
                  ))
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Time Control */}
          <div className="p-3 border-t border-white/10 bg-black/30 flex-shrink-0 space-y-2">
            <div className="flex items-center space-x-1">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={stepBackward}
                disabled={timeline.length === 0}
                className="flex-1 h-8"
              >
                <SkipBack className="w-4 h-4 mr-1" />
                Step Back
              </Button>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={forkTimeline}
                      disabled={selectedStep === null}
                      className="h-8"
                    >
                      <GitBranch className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Fork timeline at selected step</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {branches.length > 1 && (
              <div className="text-xs text-gray-500">
                Branches: {branches.map(b => b.name).join(', ')}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Event Console + Capabilities */}
        <div className="flex flex-col overflow-hidden">
          {/* Event Injection */}
          <div className="p-3 border-b border-white/10 flex-shrink-0 space-y-3">
            <div className="text-[10px] text-gray-500 uppercase tracking-wider">Dispatch Event</div>
            
            <div className="space-y-2">
              <Select 
                value={selectedEvent} 
                onValueChange={setSelectedEvent}
              >
                <SelectTrigger data-testid="event-select">
                  <SelectValue placeholder="Select event..." />
                </SelectTrigger>
                <SelectContent>
                  {availableEvents.length > 0 ? (
                    availableEvents.map((event) => (
                      <SelectItem 
                        key={event.event_name} 
                        value={event.event_name}
                      >
                        {event.event_name}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="_none" disabled>No events available</SelectItem>
                  )}
                </SelectContent>
              </Select>
              
              <Input
                value={eventPayload}
                onChange={(e) => setEventPayload(e.target.value)}
                placeholder="Payload JSON (optional)"
                className="font-mono text-xs h-8"
                data-testid="event-payload"
              />
            </div>
            
            <Button 
              onClick={dispatchEvent} 
              disabled={loading || !selectedEvent}
              className="w-full"
              data-testid="dispatch-btn"
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              {loading ? 'Dispatching...' : 'Dispatch'}
            </Button>
            
            {availableEvents.length === 0 && snapshot && (
              <div className="text-xs text-yellow-400 flex items-center">
                <AlertTriangle className="w-3 h-3 mr-1" />
                No events handled in '{snapshot.active_state}'
              </div>
            )}
          </div>

          {/* Capabilities */}
          <div className="flex-1 overflow-auto p-3">
            <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">
              Capabilities ({session.profile})
            </div>
            <div className="space-y-1">
              {['Net.HTTP', 'Storage.DO', 'Storage.KV', 'Storage.Local', 'Storage.Supabase', 'Log.Trace', 'Clock.Now', 'Auth.OAuth.Google'].map(cap => {
                const isAvailable = availableCaps.includes(cap);
                return (
                  <div key={cap} className={cn(
                    "flex items-center justify-between p-2 rounded text-xs",
                    isAvailable 
                      ? "bg-green-500/10 border border-green-500/30" 
                      : "bg-white/5 border border-white/10 opacity-50"
                  )}>
                    <span className="font-mono">{cap}</span>
                    {isAvailable ? (
                      <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 text-gray-500" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Timeline Entry Component
 */
const TimelineEntry = ({ entry, index, selected, onSelect }) => {
  if (!entry) return null;
  
  const hasEffects = entry.effects && entry.effects.length > 0;
  const hasDenied = entry.effects?.some(e => e.status === 'denied');
  const hasDeferred = entry.effects?.some(e => e.status === 'deferred');

  return (
    <button
      onClick={onSelect}
      className={cn(
        "w-full text-left p-2 rounded text-xs transition-all",
        selected 
          ? "bg-blue-500/20 border border-blue-500/50" 
          : "bg-white/5 border border-transparent hover:bg-white/10",
        !entry.transition_taken && "opacity-60"
      )}
      data-testid={`timeline-entry-${index}`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center space-x-2">
          <span className="text-gray-500 font-mono">[{entry.step_index}]</span>
          <Badge 
            variant="outline" 
            className={cn(
              "text-[10px] px-1 py-0 h-4",
              entry.transition_taken ? "text-green-400 border-green-500/30" : "text-gray-500 border-gray-500/30"
            )}
          >
            {entry.event_dispatched}
          </Badge>
        </div>
        <div className="flex items-center space-x-1">
          {hasDeferred && <AlertTriangle className="w-3 h-3 text-yellow-400" />}
          {hasDenied && <XCircle className="w-3 h-3 text-red-400" />}
        </div>
      </div>
      <div className="text-gray-400 font-mono">
        {entry.from_state} → {entry.to_state}
      </div>
      {hasEffects && (
        <div className="mt-1 flex items-center space-x-1">
          <Zap className="w-3 h-3 text-purple-400" />
          <span className="text-[10px] text-gray-500">
            {entry.effects.length} effect(s)
          </span>
        </div>
      )}
    </button>
  );
};

export default SimulationPanel;
